import React, { useState, useEffect, useRef } from "react";
import "./style.sass";
import { Evaluation } from "./evaluation";
import { useNavigate } from "react-router-dom";
import { ROUTE_DASHBOARD } from "../../route/communicative";
import assets from "../../assets";
import { useSingleton } from "../../hooks/use-singleton";
import { useHandler } from "./handler";

const TOPIC_SUGGESTIONS_BY_LEVEL = {
  Beginner: [
    "Present Simple Tense",
    "Past Simple Tense",
    "Articles (a / an / the)",
    "Prepositions (in, on, at)",
  ],
  Intermediate: [
    "Continuous & Perfect Tenses",
    "Modal Verbs (can, must, should)",
    "Active & Passive Voice",
    "Subject-Verb Agreement",
  ],
  Advanced: [
    "Conditionals (If Clauses)",
    "Direct & Indirect Speech",
    "Relative Clauses (who, which)",
    "Phrasal Verbs & Idioms",
  ],
};

const QUESTION_TYPES = [
  { id: "MCQ", label: "Multiple Choice (MCQ)" },
  { id: "Fill in the blanks", label: "Fill in Blanks" },
  { id: "Both", label: "Both (Mixed)" },
];

const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

function QuestionCountDropdown({ count, onSelectCount }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: 5, label: "5 Questions" },
    { value: 10, label: "10 Questions" },
    { value: 15, label: "15 Questions" },
    { value: 20, label: "20 Questions" },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentLabel =
    options.find((opt) => opt.value === Number(count))?.label ||
    (count ? `${count} Questions` : "Select Number of Questions");

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className={`custom-dropdown-trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="dropdown-selected-text">{currentLabel}</span>
        <span className={`dropdown-chevron ${isOpen ? "open" : ""}`}>
          <svg width="11" height="7" viewBox="0 0 12 8" fill="none">
            <path
              d="M1 1.5L6 6.5L11 1.5"
              stroke="#64748B"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="custom-dropdown-menu">
          <div className="dropdown-options-list">
            {options.map((opt) => {
              const isSelected = Number(count) === opt.value;
              return (
                <div
                  key={opt.value}
                  className={`dropdown-option-item ${isSelected ? "selected" : ""}`}
                  onClick={() => {
                    onSelectCount(opt.value);
                    setIsOpen(false);
                  }}
                >
                  <span className="option-label">{opt.label}</span>
                  {isSelected && <span className="option-check">✓</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export const Grammar = ({ onStartConversation = () => {} }) => {
  const [numQuestions, setNumQuestions] = useState(5);
  const [questionType, setQuestionType] = useState("MCQ");
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("Beginner");
  const navigate = useNavigate();
  const { mail } = useSingleton();
  const {
    grammarData,
    loading,
    setLoading,
    getGrammarExercise,
    showEvaluation,
    setShowEvaluation,
  } = useHandler();

  const isReady = numQuestions && questionType && topic.trim() && difficulty;
  const currentSuggestions = TOPIC_SUGGESTIONS_BY_LEVEL[difficulty] || TOPIC_SUGGESTIONS_BY_LEVEL.Beginner;

  const handleStart = async () => {
    if (!isReady) return;

    const data = await getGrammarExercise({
      topic,
      difficulty,
      questionType,
      numQuestions,
      userMail: mail,
    });

    if (data) {
      setShowEvaluation(true);
    }
  };

  if (showEvaluation) {
    return (
      <Evaluation
        grammarData={grammarData}
        questionType={questionType}
        topic={topic}
        difficulty={difficulty}
        onBackToSetup={() => setShowEvaluation(false)}
      />
    );
  }

  return (
    <div className="grammar-setup-page">
      <header className="grammar-header">
        <div className="grammar-header__brand">
          <img src={assets.ic_logo} alt="Rovian AI" className="logo" />
          <span className="brand_name">Rovian AI</span>
        </div>
        <div
          className="grammar-header__dashboard-link"
          onClick={() => navigate(ROUTE_DASHBOARD)}
        >
          <span className="back-arrow">←</span>
          <span>Dashboard</span>
        </div>
      </header>

      <div className="grammar-setup-body">
        <main className="grammar-setup-card">
          <div className="grammar-card-header">
            <h1 className="grammar-setup__title">Grammar Practice</h1>
            <p className="grammar-setup__subtitle">
              Choose difficulty, topic, and format to begin practice.
            </p>
          </div>

          {/* 1. DIFFICULTY */}
          <section className="field">
            <label className="field__label">Difficulty Level</label>
            <div className="difficulty-grid">
              {DIFFICULTIES.map((level) => {
                const isActive = difficulty === level;
                return (
                  <button
                    key={level}
                    type="button"
                    className={`difficulty-btn ${isActive ? "active" : ""}`}
                    onClick={() => setDifficulty(level)}
                  >
                    <span className="difficulty-name">{level}</span>
                    {isActive && <span className="selection-dot"></span>}
                  </button>
                );
              })}
            </div>
          </section>

          {/* 2. TOPIC & SUGGESTIONS */}
          <section className="field">
            <div className="field-label-row">
              <label className="field__label" htmlFor="topic-input">
                Topic
              </label>
              <span className="field-hint">Pick below or type custom</span>
            </div>

            <div className="topic-input-wrapper">
              <input
                id="topic-input"
                type="text"
                className="text-input"
                placeholder="e.g. Present Simple Tense, Prepositions..."
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
              {topic && (
                <button
                  type="button"
                  className="topic-clear-btn"
                  onClick={() => setTopic("")}
                  title="Clear topic"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="topic-pills-grid">
              {currentSuggestions.map((suggestion) => {
                const isSelected = topic.trim().toLowerCase() === suggestion.toLowerCase();
                return (
                  <button
                    key={suggestion}
                    type="button"
                    className={`topic-pill ${isSelected ? "selected" : ""}`}
                    onClick={() => setTopic(suggestion)}
                  >
                    <span className="pill-dot"></span>
                    <span className="pill-text">{suggestion}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* 3. QUESTION TYPE */}
          <section className="field">
            <label className="field__label">Question Type</label>
            <div className="question-types-grid">
              {QUESTION_TYPES.map((type) => {
                const isActive = questionType === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    className={`type-btn ${isActive ? "active" : ""}`}
                    onClick={() => setQuestionType(type.id)}
                  >
                    <span className="type-label">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* 4. NUMBER OF QUESTIONS */}
          <section className="field">
            <label className="field__label">Number of Questions</label>
            <QuestionCountDropdown
              count={numQuestions}
              onSelectCount={(val) => setNumQuestions(val)}
            />
          </section>

          {/* START BUTTON */}
          <button
            type="button"
            className={`start-button ${loading ? "loading" : ""}`}
            disabled={!isReady || loading}
            onClick={handleStart}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Generating Practice...
              </>
            ) : (
              "Start Grammar Practice"
            )}
          </button>
        </main>
      </div>
    </div>
  );
};
